import { z } from 'zod';
import type { ToolReadingDto } from './gaps';
import {
  INNOVATION_KEYS, INNOVATION_TYPES, POTENTIALS,
  type Inovacao, type InnovationInsight, type InnovationType, type Potential,
} from './strategy';

/* ─────────────────────────────────────────────────────────────────────
   10 Tipos de Inovação (Doblin) — o diagnóstico, as contas e as leituras.

   Diagnóstico: maturidade atual e desejada de cada tipo, de 1 a 5, com
   evidências. As contas (gap, maiores oportunidades) são puras. A leitura
   inicial da IA sai do fim do preenchimento, sem ler as evidências.

   Insights: um ou mais por tipo, da organização ou da IA. A cada rodada de
   leitura das evidências a IA traz insights novos — que entram na lista
   pela tela, uma vez só (o id da rodada fica guardado).
   ───────────────────────────────────────────────────────────────────── */

export const INNOVATION_CATEGORIES = [
  { key: 'configuracao', label: 'Configuração', types: ['profit', 'network', 'structure', 'process'] },
  { key: 'oferta', label: 'Oferta', types: ['performance', 'system'] },
  { key: 'experiencia', label: 'Experiência', types: ['service', 'channel', 'brand', 'engagement'] },
] as const satisfies ReadonlyArray<{ key: string; label: string; types: readonly InnovationType[] }>;
export type InnovationCategory = (typeof INNOVATION_CATEGORIES)[number]['key'];

export const INNOVATION_META: Record<InnovationType, { label: string; short: string; desc: string; category: InnovationCategory }> = {
  profit:      { label: 'Modelo de Lucro', short: 'ML', desc: 'Novos fluxos de receita e modelos de negócio.', category: 'configuracao' },
  network:     { label: 'Rede', short: 'Rede', desc: 'Ecossistema de parceiros e colaboração.', category: 'configuracao' },
  structure:   { label: 'Estrutura', short: 'Estrutura', desc: 'Organização, talentos e ativos.', category: 'configuracao' },
  process:     { label: 'Processo', short: 'Processo', desc: 'Métodos e rotinas de trabalho.', category: 'configuracao' },
  performance: { label: 'Desempenho de Produto', short: 'DP', desc: 'Qualidade e diferenciação do produto.', category: 'oferta' },
  system:      { label: 'Sistema de Produto', short: 'SP', desc: 'Integração de produtos e serviços.', category: 'oferta' },
  service:     { label: 'Serviços', short: 'Serviços', desc: 'Suporte e melhoria da experiência.', category: 'experiencia' },
  channel:     { label: 'Canal', short: 'Canal', desc: 'Formas de entrega e acesso.', category: 'experiencia' },
  brand:       { label: 'Marca', short: 'Marca', desc: 'Posicionamento e percepção de valor.', category: 'experiencia' },
  engagement:  { label: 'Envolvimento do Cliente', short: 'Cliente', desc: 'Relacionamento e cocriação.', category: 'experiencia' },
};
export const MATURITY_LEVEL: Record<number, string> = { 1: 'Muito baixa', 2: 'Baixa', 3: 'Média', 4: 'Alta', 5: 'Muito alta' };
export const POTENTIAL_LABEL: Record<Potential, string> = { alto: 'Alto', medio: 'Médio', baixo: 'Baixo' };

/** Potencial 0–100: a distância até a meta pesa 70%; a ambição da meta, 30%. */
export const innovationScore = (current: number, target: number) =>
  (current > 0 && target > 0 ? Math.max(0, Math.min(100, Math.round(((Math.max(0, target - current) / 4) * 70) + (target / 5) * 30))) : 0);

/**
 * Os dez tipos, sempre, na ordem; o potencial derivado; ids nos insights.
 * Projeto antigo (só potencial e nota): as notas viram insights da organização.
 */
export function withInovacaoDerived(c: Inovacao): Inovacao {
  const by = new Map(c.types.map(t => [t.key, t]));
  const types = INNOVATION_KEYS.map(key => {
    const t = by.get(key) ?? { key, current: 0, target: 0, score: 0, note: '' };
    return { ...t, score: t.current > 0 && t.target > 0 ? innovationScore(t.current, t.target) : t.current || t.target ? 0 : t.score };
  });
  let insights = c.insights;
  if (!insights.length && c.types.some(t => t.note.trim())) {
    insights = c.types.filter(t => t.note.trim()).map(t => ({ type: t.key, text: t.note.trim(), potential: (t.score >= 70 ? 'alto' : t.score >= 50 ? 'medio' : 'baixo') as Potential, ai: false }));
  }
  const used = new Set<string>();
  let n = 0;
  insights = insights.map(i => {
    let id = i.id && !used.has(i.id) ? i.id : '';
    while (!id || used.has(id)) id = `ins-${++n}`;
    used.add(id);
    return { ...i, id };
  });
  return { ...c, types, insights };
}

export interface InnovationRow { key: InnovationType; label: string; short: string; current: number; target: number; gap: number; score: number }

/** As contas do diagnóstico: por tipo, os maiores gaps, as categorias. */
export function innovationMetrics(c: Inovacao) {
  const rows: InnovationRow[] = INNOVATION_TYPES.map(([key]) => {
    const t = c.types.find(x => x.key === key);
    const current = t?.current ?? 0, target = t?.target ?? 0;
    return { key, label: INNOVATION_META[key].label, short: INNOVATION_META[key].short, current, target, gap: current && target ? Math.max(0, target - current) : 0, score: t?.score ?? 0 };
  });
  const rated = rows.filter(r => r.current > 0 && r.target > 0);
  const top = [...rated].filter(r => r.gap > 0).sort((a, b) => b.gap - a.gap || b.target - a.target || INNOVATION_KEYS.indexOf(a.key) - INNOVATION_KEYS.indexOf(b.key)).slice(0, 5);
  const cats = INNOVATION_CATEGORIES.map(cat => {
    const rs = rated.filter(r => (cat.types as readonly string[]).includes(r.key));
    const avg = (f: (r: InnovationRow) => number) => (rs.length ? rs.reduce((s, r) => s + f(r), 0) / rs.length : 0);
    return { key: cat.key, label: cat.label, current: avg(r => r.current), target: avg(r => r.target), gap: avg(r => r.gap) };
  });
  return { rows, rated: rated.length, complete: rated.length === rows.length, top, cats };
}
export type InnovationMetrics = ReturnType<typeof innovationMetrics>;

/** O diagnóstico está pronto para a leitura inicial: os dez tipos com atual e desejada. */
export const innovationDiagnosed = (c: Inovacao) => innovationMetrics(c).complete;

/* ── as leituras do agente ────────────────────────────────────────── */

/** Leitura inicial: do diagnóstico preenchido, sem as evidências. */
export const innovationReadingSchema = z.object({
  leitura: z.string().trim().min(20).max(600),
  /** o quanto os gaps escolhidos servem ao propósito e ao objetivo, 0–100 */
  alinhamento: z.number().int().min(0).max(100),
});
export type InnovationReading = z.infer<typeof innovationReadingSchema>;
export type InnovationReadingDto = ToolReadingDto<InnovationReading>;

/** Uma rodada de insights: novos insights, focos e o potencial adicional. */
export const innovationBatchSchema = z.object({
  insights: z.array(z.object({
    type: z.enum(INNOVATION_KEYS),
    text: z.string().trim().min(12).max(400),
    potential: z.enum(POTENTIALS).default('medio'),
  })).max(8),
  focos: z.array(z.object({ titulo: z.string().trim().min(3).max(80), texto: z.string().trim().min(8).max(220) })).max(3).default([]),
  potencial: z.object({
    score: z.number().int().min(0).max(100),
    nivel: z.enum(POTENTIALS),
    texto: z.string().trim().max(280).default(''),
  }),
});
export type InnovationBatchOutput = z.infer<typeof innovationBatchSchema>;
/** O que fica guardado: a rodada, com id (para entrar na lista uma vez só). */
export interface InnovationBatch extends InnovationBatchOutput { id: string; at: string; evidences: number; /** as evidências lidas que esta rodada já considerou */ evIds: string[] }
export type InnovationBatchDto = ToolReadingDto<InnovationBatch>;

/** Junta uma rodada da IA aos insights da lista — sem repetir texto, uma vez por rodada. */
export function mergeInnovationBatch(c: Inovacao, b: InnovationBatch): Inovacao {
  if (c.batches.includes(b.id)) return c;
  const fold = (s: string) => s.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase().replace(/\s+/g, ' ').trim();
  const have = new Set(c.insights.map(i => fold(i.text)));
  const used = new Set(c.insights.map(i => i.id));
  let n = c.insights.length;
  const add: InnovationInsight[] = [];
  for (const x of b.insights) {
    if (have.has(fold(x.text))) continue;
    have.add(fold(x.text));
    let id = '';
    do id = `ins-${++n}`; while (used.has(id));
    used.add(id);
    add.push({ id, type: x.type, text: x.text, potential: x.potential, ai: true });
  }
  return { ...c, insights: [...c.insights, ...add].slice(0, 60), batches: [...c.batches, b.id].slice(-80) };
}
